// ─── LRC Digital — Google Apps Script API Service ───────────────────────────
// Endpoint: Google Apps Script Web App (deployed as "Anyone" access)
// Handles: lead submissions, blog post fetching, project queries, admin ops

const API_URL =
  'https://script.google.com/macros/s/AKfycbxJszv_FmjfkXSlcB-gMniPLlh8SVXAWZrxgl1udbB8Y2QqFOVZeS_R6mNpBiiIuDdnZg/exec';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BlogPost {
  id?: string;
  row?: number;
  slug: string;
  title: string;
  date: string;
  category: string;
  excerpt: string;
  content: string;
  tags?: string;
  readTime?: string;
  author?: string;
  featuredImageUrl?: string;
}

export interface Lead {
  row?: number;
  timestamp?: string;
  fullName: string;
  email: string;
  phone?: string;
  businessName: string;
  revenueTier: string;
  industry?: string;
  teamSize?: string;
  painPoint?: string;
  vision?: string;
  investmentReadiness?: string;
  consultFormat?: string;
  status?: string;
  notes?: string;
}

export interface Project {
  row?: number;
  projectId: string;
  clientName: string;
  track: string;
  status: string;
  startDate?: string;
  nextMilestone?: string;
  visualBufferUrl?: string;
  notes?: string;
}

export interface LeadPayload {
  businessName: string;
  revenueTier: string;
  industry: string;
  teamSize: string;
  painPoint: string;
  currentTools: string;
  hoursLost: string;
  brokenSystems: string[];
  vision: string;
  investmentReadiness: string;
  howFound: string;
  fullName: string;
  email: string;
  phone: string;
  consultFormat: string;
}

export interface ApiResponse<T = unknown> {
  status: 'success' | 'error';
  message?: string;
  data?: T;
}

// ─── Shared POST helper ────────────────────────────────────────────────────────
async function apiPost<T = unknown>(body: object): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}?token=Bossbabe2026##`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'text/plain;charset=utf-8'
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ─── Shared GET helper ─────────────────────────────────────────────────────────
async function apiGet<T>(sheet: string): Promise<T[]> {
  const res = await fetch(`${API_URL}?sheet=${sheet}&token=Bossbabe2026##`, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json: ApiResponse<T[]> = await res.json();
  if (json.status === 'success' && Array.isArray(json.data)) return json.data;
  throw new Error(json.message || `Could not load ${sheet}`);
}

// Helper to dynamically assign blog categories based on content and keyword matches
function determineCategory(title: string, content: string, tags: string, existingCategory?: string): string {
  const normalizedExisting = (existingCategory || '').trim();
  if (normalizedExisting && ['FlowState', 'AI Systems', 'Operations', 'Sovereignty'].includes(normalizedExisting)) {
    return normalizedExisting;
  }
  
  const combined = `${title} ${content} ${tags}`.toLowerCase();
  
  if (combined.includes('sovereignty') || combined.includes('sovereign') || combined.includes('freedom') || combined.includes('sanctuary')) {
    return 'Sovereignty';
  }
  if (combined.includes('flowstate') || combined.includes('flow state') || combined.includes('somatic') || combined.includes('nervous') || combined.includes('body')) {
    return 'FlowState';
  }
  if (combined.includes('operations') || combined.includes('onboarding') || combined.includes('overhead') || combined.includes('crm') || combined.includes('booking') || combined.includes('workspace')) {
    return 'Operations';
  }
  
  return 'AI Systems';
}

// ── Public: Blog Posts ─────────────────────────────────────────────────────────
export async function getBlogPosts(): Promise<BlogPost[]> {
  const data = await apiGet<any>('Blog');
  return data.map((post: any, idx: number) => {
    const titleVal = post.Title || post.title || '';
    const contentVal = post.HTML_Content || post.content || '';
    const tagsVal = post.Tags || post.tags || '';
    const categoryVal = post.Category || post.category || '';
    
    return {
      row: post.row || (idx + 2),
      slug: post.Post_ID || post.slug || '',
      title: titleVal,
      date: post.Publish_Date ? new Date(post.Publish_Date).toISOString().split('T')[0] : (post.date || ''),
      category: determineCategory(titleVal, contentVal, tagsVal, categoryVal),
      excerpt: post.Meta_Description || post.excerpt || post.meta || '',
      content: contentVal,
      tags: tagsVal,
      readTime: post.Read_Time || post.readTime || '6 min read',
      author: post.Author || post.author || "La' Toya Renee Carter",
      featuredImageUrl: post.Featured_Image_URL || post.image_url || '',
    };
  });
}

// ── Public: Lead Submission ───────────────────────────────────────────────────
export async function submitLead(payload: LeadPayload): Promise<ApiResponse> {
  return apiPost({ action: 'submit_lead', ...payload });
}

// ── Public: Newsletter Subscription ────────────────────────────────────────────
export async function subscribeNewsletter(name: string, email: string): Promise<ApiResponse> {
  return apiPost({ action: 'subscribe', name, email });
}

// ── Admin: Get All Leads ──────────────────────────────────────────────────────
export async function getLeads(): Promise<Lead[]> {
  return apiGet<Lead>('Leads');
}

// ── Admin: Update Lead Status ─────────────────────────────────────────────────
export async function updateLeadStatus(
  row: number,
  status: string,
  notes?: string
): Promise<ApiResponse> {
  return apiPost({ action: 'update_lead_status', row, status, notes });
}

// ── Admin: Get All Projects ───────────────────────────────────────────────────
export async function getProjects(): Promise<Project[]> {
  return apiGet<Project>('Projects');
}

// ── Admin: Update Project Status ──────────────────────────────────────────────
export async function updateProject(
  projectId: string,
  newStatus: string
): Promise<ApiResponse> {
  return apiPost({ action: 'update_project', projectId, status: newStatus });
}

// ── Admin: Add New Blog Post ──────────────────────────────────────────────────
export async function addBlogPost(post: Omit<BlogPost, 'id' | 'row'>): Promise<ApiResponse> {
  return apiPost({ action: 'add_post', ...post });
}

// ── Admin: Delete Blog Post ───────────────────────────────────────────────────
export async function deleteBlogPost(row: number): Promise<ApiResponse> {
  return apiPost({ action: 'delete_post', row });
}

// ── Admin: Get Summary Stats ──────────────────────────────────────────────────
export async function getStats(): Promise<{ leads: number; posts: number; projects: number }> {
  const [leads, posts, projects] = await Promise.allSettled([
    getLeads(),
    getBlogPosts(),
    getProjects(),
  ]);
  return {
    leads: leads.status === 'fulfilled' ? leads.value.length : 0,
    posts: posts.status === 'fulfilled' ? posts.value.length : 0,
    projects: projects.status === 'fulfilled' ? projects.value.length : 0,
  };
}

// ── Public: ArtisanFlow Early Access Lead Submission ───────────────────────────
export async function submitArtisanFlowLead(name: string, email: string): Promise<ApiResponse> {
  return apiPost({ action: 'artisan_flow_lead', name, email });
}

// ── Public: Request Custom Build Intake Form Submission ───────────────────────
export async function submitRequestBuild(payload: {
  name: string;
  email: string;
  company: string;
  industry: string;
  friction: string;
  vision: string;
  investment: string;
  timeline: string;
}): Promise<ApiResponse> {
  return apiPost({ action: 'request_custom_build', ...payload });
}

// ── Public: QRSafe Early Access Lead Submission ───────────────────────────────
export async function submitQRSafeLead(name: string, email: string): Promise<ApiResponse> {
  return apiPost({ action: 'qr_safe_lead', name, email });
}

// ── Public: Instant Purchase (Security & Conversion Audit) ───────────────────
export async function submitSecurityAuditPurchase(payload: {
  name: string;
  email: string;
  company: string;
  amount: number;
  paymentToken?: string;
}): Promise<ApiResponse> {
  return apiPost({ action: 'security_audit_purchase', ...payload });
}

// ── Public: Abandoned Cart / Intent Recovery Logger ───────────────────────────
export async function logAbandonedCart(payload: {
  name: string;
  email: string;
  company?: string;
  item?: string;
  step?: string;
}): Promise<ApiResponse> {
  return apiPost({ action: 'abandoned_cart', ...payload });
}

